import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth";
import DayView from "@/components/DayView";
import PlannerHeader from "@/components/PlannerHeader";
import DayTabs from "@/components/DayTabs";
import NewMonthDialog from "@/components/NewMonthDialog";
import ImportDialog from "@/components/ImportDialog";
import MasterDataDialog from "@/components/MasterDataDialog";
import GeneralMenuDialog from "@/components/GeneralMenuDialog";
import { usePresence } from "@/hooks/usePresence";
import { useMonths } from "@/hooks/useMonths";
import { usePlannerShortcuts } from "@/hooks/usePlannerShortcuts";
import { usePlannerDialogs } from "@/hooks/usePlannerDialogs";

function PlannerDialogs({ show, months, onClose, onCreated, onChanged }) {
  return (
    <>
      <NewMonthDialog open={show.newMonth} onClose={() => onClose("newMonth")} onCreated={onCreated} />
      <ImportDialog open={show.import} onClose={() => onClose("import")} />
      <MasterDataDialog open={show.master} onClose={() => onClose("master")} />
      <GeneralMenuDialog open={show.menu} onClose={() => onClose("menu")} months={months} onChanged={onChanged} />
    </>
  );
}

function NoMonthMessage() {
  return (
    <div className="p-10 text-slate-500 text-sm" data-testid="no-month-message">
      Nessun mese disponibile. Crea un nuovo mese (es. 26 08) o importa le anagrafiche da XLSM.
    </div>
  );
}

export default function Planner() {
  const { operator, logout } = useAuth();
  const [day, setDay] = useState(() => new Date().getDate()); // all'apertura: giorno corrente
  const { showConfig, openDialog, closeDialog } = usePlannerDialogs();
  const currentKeyRef = useRef(null);
  const { months, currentKey, setCurrentKey, monthInfo, loadMonths } = useMonths(currentKeyRef);
  currentKeyRef.current = currentKey;
  const { online, refreshKey } = usePresence(currentKeyRef);
  usePlannerShortcuts(monthInfo, setDay);

  // deep-link: ?mese=YY%20MM&giorno=N (usato anche dal rendering PDF headless)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const mese = q.get("mese");
    const giorno = parseInt(q.get("giorno") || "", 10);
    if (mese) setCurrentKey(mese);
    if (giorno) setDay(giorno);
    // eseguito una sola volta all'avvio
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (monthInfo) setDay((d) => Math.min(d, monthInfo.days.length));
    // setDay è stabile (React): deps limitate a monthInfo
  }, [monthInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCreated = (key) => { loadMonths().then(() => { setCurrentKey(key); setDay(1); }); };

  return (
    <div className="min-h-screen bg-slate-50">
      <PlannerHeader
        months={months}
        currentKey={currentKey}
        onSelectMonth={(key) => { setCurrentKey(key); setDay(1); }}
        onNewMonth={() => openDialog("newMonth")}
        onImport={() => openDialog("import")}
        onMaster={() => openDialog("master")}
        onMenu={() => openDialog("menu")}
        others={online.filter((n) => n !== operator)}
        operator={operator}
        onLogout={logout}
      />

      {monthInfo && <DayTabs days={monthInfo.days} day={day} onSelect={setDay} />}

      {currentKey && monthInfo ? (
        <DayView key={`${currentKey}-${day}`} monthKey={currentKey} dayNum={day} refreshKey={refreshKey} numDays={monthInfo.days.length} months={months} />
      ) : (
        <NoMonthMessage />
      )}

      <PlannerDialogs
        show={showConfig}
        months={months}
        onClose={closeDialog}
        onCreated={onCreated}
        onChanged={loadMonths}
      />
    </div>
  );
}
